import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    Grid, Card, CardContent, IconButton, Chip, Divider, TextField, Alert,
    InputAdornment, Snackbar
} from '@mui/material';
import {
    Close as CloseIcon, Share as ShareIcon, ContentCopy as CopyIcon,
    Facebook as FacebookIcon, LinkedIn as LinkedInIcon,
    WhatsApp as WhatsAppIcon, Reddit as RedditIcon,
    Link as LinkIcon, Email as EmailIcon
} from '@mui/icons-material';

const SocialButton = ({ icon, label, color, onClick, description }) => (
    <Card
        sx={{
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
                borderColor: color
            }
        }}
        onClick={onClick}
    >
        <CardContent sx={{ p: 2, textAlign: 'center' }}>
            <Box sx={{ color: color, mb: 1 }}>
                {icon}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </CardContent>
    </Card>
);

const ShareDialog = ({ open, onClose, shareType = 'platform', statistics, badges, achievements }) => {
    const [customMessage, setCustomMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const getShareContent = () => {
        const baseUrl = window.location.origin + window.location.pathname;
        
        switch (shareType) {
            case 'achievement':
                const latestBadge = achievements?.[0];
                return {
                    title: `🏆 New Achievement Unlocked!`,
                    description: `I just earned the "${latestBadge?.name}" badge on EndoLink - a platform for preserving linguistic heritage! 🌍`,
                    url: baseUrl,
                    hashtags: ['EndoLink', 'Linguistics', 'Research', 'Achievement'],
                    emoji: '🏆'
                };
            
            case 'stats':
                const contributionCount = statistics?.totalContributions || 0;
                const languageCount = statistics?.languagesContributed?.size || 0;
                return {
                    title: `📊 My Research Progress`,
                    description: `I've contributed ${contributionCount} place names in ${languageCount} languages on EndoLink! Help preserve linguistic heritage with me 🌍`,
                    url: baseUrl,
                    hashtags: ['EndoLink', 'Linguistics', 'Research', 'Progress'],
                    emoji: '📊'
                };
            
            case 'milestone':
                return {
                    title: `🎯 Milestone Reached!`,
                    description: `Just reached ${statistics?.totalContributions} contributions on EndoLink! Every place name helps preserve our linguistic heritage 🌍`,
                    url: baseUrl,
                    hashtags: ['EndoLink', 'Linguistics', 'Milestone', 'Research'],
                    emoji: '🎯'
                };
            
            default: // 'platform'
                return {
                    title: `🌍 Discover EndoLink`,
                    description: `Check out EndoLink - an innovative platform for preserving linguistic heritage! Help researchers document place names in local languages 🏛️`,
                    url: baseUrl,
                    hashtags: ['EndoLink', 'Linguistics', 'Research', 'Heritage'],
                    emoji: '🌍'
                };
        }
    };

    const shareContent = getShareContent();
    const fullMessage = customMessage || shareContent.description;

    const socialPlatforms = [
        {
            name: 'Facebook',
            icon: <FacebookIcon sx={{ fontSize: 32 }} />,
            color: '#4267B2',
            description: 'Post to your timeline',
            action: () => {
                const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareContent.url)}&quote=${encodeURIComponent(fullMessage)}`;
                window.open(url, '_blank', 'width=600,height=400');
            }
        },
        {
            name: 'LinkedIn',
            icon: <LinkedInIcon sx={{ fontSize: 32 }} />,
            color: '#0077B5',
            description: 'Share professionally',
            action: () => {
                const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareContent.url)}&title=${encodeURIComponent(shareContent.title)}&summary=${encodeURIComponent(fullMessage)}`;
                window.open(url, '_blank', 'width=600,height=400');
            }
        },
        {
            name: 'WhatsApp',
            icon: <WhatsAppIcon sx={{ fontSize: 32 }} />,
            color: '#25D366',
            description: 'Send to contacts',
            action: () => {
                const text = `${fullMessage}\n\n${shareContent.url}`;
                const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
                window.open(url, '_blank');
            }
        },
        {
            name: 'Reddit',
            icon: <RedditIcon sx={{ fontSize: 32 }} />,
            color: '#FF4500',
            description: 'Post to communities',
            action: () => {
                const url = `https://reddit.com/submit?url=${encodeURIComponent(shareContent.url)}&title=${encodeURIComponent(shareContent.title)}`;
                window.open(url, '_blank');
            }
        },
        {
            name: 'Email',
            icon: <EmailIcon sx={{ fontSize: 32 }} />,
            color: '#EA4335',
            description: 'Send via email',
            action: () => {
                const subject = encodeURIComponent(shareContent.title);
                const body = encodeURIComponent(`${fullMessage}\n\nVisit: ${shareContent.url}`);
                const url = `mailto:?subject=${subject}&body=${body}`;
                window.open(url);
            }
        }
    ];

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setSnackbarOpen(true);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                setSnackbarOpen(true);
            } catch (fallbackErr) {
                console.error('Failed to copy text: ', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const getShareTypeTitle = () => {
        switch (shareType) {
            case 'achievement': return '🏆 Share Achievement';
            case 'stats': return '📊 Share Progress';
            case 'milestone': return '🎯 Share Milestone';
            default: return '🌍 Share EndoLink';
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    m: 0
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShareIcon />
                        <Typography variant="h6" component="div">
                            {getShareTypeTitle()}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {/* Preview */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {shareContent.emoji} Preview
                        </Typography>
                        <Card sx={{ p: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                {shareContent.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {fullMessage}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="caption" color="primary.main">
                                    {shareContent.url}
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                {shareContent.hashtags.map(tag => (
                                    <Chip
                                        key={tag}
                                        label={`#${tag}`}
                                        size="small"
                                        sx={{ mr: 0.5, mt: 0.5, fontSize: '0.7rem' }}
                                        variant="outlined"
                                        color="primary"
                                    />
                                ))}
                            </Box>
                        </Card>
                    </Box>

                    {/* Custom Message */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Customize Your Message
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.target.value)}
                            placeholder={shareContent.description}
                            variant="outlined"
                            sx={{ mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Leave empty to use the default message
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Social Media Platforms */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Choose Platform
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {socialPlatforms.map((platform) => (
                            <Grid item xs={6} sm={4} md={3} key={platform.name}>
                                <SocialButton
                                    icon={platform.icon}
                                    label={platform.name}
                                    color={platform.color}
                                    description={platform.description}
                                    onClick={platform.action}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <Divider sx={{ mb: 3 }} />

                    {/* Direct Link Sharing */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Direct Link
                    </Typography>
                    <TextField
                        fullWidth
                        value={shareContent.url}
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => copyToClipboard(shareContent.url)}
                                        edge="end"
                                        color="primary"
                                    >
                                        <CopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1 }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<CopyIcon />}
                        onClick={() => copyToClipboard(`${fullMessage}\n\n${shareContent.url}`)}
                        sx={{ mr: 1 }}
                    >
                        Copy Message & Link
                    </Button>

                    {shareType === 'platform' && (
                        <Alert severity="info" sx={{ mt: 3 }}>
                            <Typography variant="body2">
                                Help spread the word about EndoLink! Share with researchers, linguists, and anyone interested in preserving linguistic heritage.
                            </Typography>
                        </Alert>
                    )}

                    {shareType === 'achievement' && (
                        <Alert severity="success" sx={{ mt: 3 }}>
                            <Typography variant="body2">
                                🎉 Congratulations on your achievement! Share your progress and inspire others to contribute to linguistic research.
                            </Typography>
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={onClose} variant="contained" size="large">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message="Copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

export default ShareDialog;